<template>
  <div class="flex flex-col sm:flex-row overflow-y-auto h-full">
    <div class="sm:hidden w-full text-center">
      <h2 class="py-2 flex flex-row justify-center">
        {{ $t("message.marketplaces.title") }}
        <BaseMiniSpinner class="ml-2" v-if="loading" />
      </h2>
    </div>
    <BaseProductModal
      v-model="showProductModal"
      @closeProduct="onCloseProduct"
      :code="openedProduct"
      @productUpdated="checkProductUpdate"
    />
    <template>
      <BaseLeftPageSubMenu
        :menuItems="ReportType"
        :selectedStatus="filters.type"
        :searchProp="filters.search"
        @selectedStatusFilter="updateStatusFilter"
        @searchUpdated="updateSearchFilter"
      />
      <div class="content w-full flex-grow flex-col px-4">
        <div class="header hidden sm:flex py-4 pl-4 flex flex-row">
          <div>
            {{ $t("message.marketplaces.title") }}
          </div>
          <div class="ml-4" v-if="loading">
            <BaseMiniSpinner />
          </div>
        </div>
        <AmazonCatalogReconciliation v-if="isCatalog" class="my-4" />
        <div class="flex flex-row my-4" v-if="!isCatalog">
          <button
            class="btn btn-dark-button"
            :class="{ 'btn-success-button': filters.marketplace == null }"
            @click="filters.marketplace = null"
          >
            {{ $t("message.marketplaces.any") }}
          </button>
          <button
            class="btn btn-dark-button ml-4"
            :class="{ 'btn-success-button': filters.marketplace == 'amazon' }"
            @click="filters.marketplace = 'amazon'"
          >
            {{ $t("message.marketplaces.amazon") }}
          </button>
          <button
            class="btn btn-dark-button ml-4"
            :class="{ 'btn-success-button': filters.marketplace == 'ebay' }"
            @click="filters.marketplace = 'ebay'"
          >
            {{ $t("message.marketplaces.ebay") }}
          </button>
          <button
            v-if="
              filters.marketplace == 'amazon' && userCanEditMarketplacesItems
            "
            class="btn ml-auto flex flex-row items-center"
            :class="
              bulkSyncing
                ? 'btn-dark-button opacity-50 cursor-not-allowed'
                : 'btn-success-button'
            "
            :disabled="bulkSyncing"
            @click="bulkSyncAmazon()"
          >
            <BaseMiniSpinner v-if="bulkSyncing" class="mr-2" />
            {{ $t("message.marketplaces.sync_selected_to_amazon") }}
          </button>
        </div>
        <div
          class="table w-full flex flex-col justify-center items-center"
          v-if="!isCatalog"
        >
          <!-- Vista normal: items de marketplace -->
          <template v-if="!isPublishable && !isDrift">
            <div class="w-full mx-0 my-4 px-10 grid grid-cols-9 w-full gap-1">
              <div>{{ $t("message.marketplaces.sku_nkr") }}</div>
              <div>
                {{ $t("message.marketplaces.alias") }}
                <br />
                ({{ $t("message.marketplaces.sku_marketplace") }})
              </div>
              <div>
                {{ $t("message.marketplaces.marketplace") }}
              </div>
              <div>
                {{ $t("message.marketplaces.marketplace_id") }}
              </div>
              <div class="text-center">
                {{ $t("message.marketplaces.adjustment_percent") }}
              </div>
              <div class="text-center">
                {{ $t("message.marketplaces.adjustment_fixed") }}
              </div>
              <div class="text-center">
                {{ $t("message.marketplaces.export_enabled") }}
              </div>
              <div class="text-center">
                {{ $t("message.marketplaces.fba") }}
              </div>
              <div class="text-center">
                {{ $t("message.marketplaces.actions") }}
              </div>
            </div>
            <ProductMarketplacesManagerItem
              v-for="(product, index) in products"
              :key="product.id"
              :item="product"
              :enableEditNkrCode="true"
              @updated="
                item => {
                  products[index] = item;
                }
              "
              @deleted="
                () => {
                  products = products.filter(x => x.id !== product.id);
                }
              "
            />
          </template>

          <!-- Pestaña Publicables: productos con stock no publicados en Amazon -->
          <template v-else-if="isPublishable">
            <div
              v-if="!loading && products.length === 0"
              class="py-8 text-gray-500"
            >
              {{ $t("message.marketplaces.publishable_empty") }}
            </div>
            <div
              class="w-full mx-0 my-4 px-10 grid grid-cols-6 w-full gap-1 font-bold border-b"
              v-if="products.length"
            >
              <div
                v-for="header in publishableHeaders"
                :key="header.value"
                class="flex items-center"
                :class="[
                  header.center ? 'justify-center text-center' : '',
                  header.sortable ? 'cursor-pointer select-none' : ''
                ]"
                @click="header.sortable && changeArrowDir(header.value)"
              >
                {{ $t("message.marketplaces." + header.label) }}
                <span
                  v-if="header.sortable && filters.orderCol == header.value"
                  class="ml-1"
                >
                  <img
                    :src="require(`@/assets/icons/${arrowDirection}-arrow.svg`)"
                  />
                </span>
              </div>
            </div>
            <div
              v-for="product in products"
              :key="product.nkrCode"
              class="w-full mx-0 px-10 py-2 grid grid-cols-6 w-full gap-1 items-center border-b"
            >
              <div>{{ product.nkrCode }}</div>
              <div class="truncate" :title="product.title">
                {{ product.title }}
              </div>
              <div class="text-center">{{ product.stock }}</div>
              <div class="text-center">{{ product.sold }}</div>
              <div class="text-center">${{ product.calculatedPrice }}</div>
              <div class="text-center">
                <button
                  class="btn btn-success-button"
                  @click="openProduct(product.nkrCode)"
                >
                  {{ $t("message.marketplaces.publish") }}
                </button>
              </div>
            </div>
          </template>

          <!-- Pestaña Drift: precio/stock local vs Amazon -->
          <template v-else-if="isDrift">
            <div
              v-if="!loading && products.length === 0"
              class="py-8 text-gray-500"
            >
              {{ $t("message.marketplaces.drift_empty") }}
            </div>
            <div
              v-if="products.length"
              class="w-full mx-0 my-4 px-10 grid grid-cols-6 w-full gap-1 font-bold border-b"
            >
              <div>{{ $t("message.marketplaces.sku_nkr") }}</div>
              <div class="text-center">
                {{ $t("message.marketplaces.drift_local_price") }}
              </div>
              <div class="text-center">
                {{ $t("message.marketplaces.drift_amazon_price") }}
              </div>
              <div class="text-center">
                {{ $t("message.marketplaces.drift_local_stock") }}
              </div>
              <div class="text-center">
                {{ $t("message.marketplaces.drift_amazon_stock") }}
              </div>
              <div class="text-center">
                {{ $t("message.marketplaces.fba") }}
              </div>
            </div>
            <div
              v-for="product in products"
              :key="product.sku"
              class="w-full mx-0 px-10 py-2 grid grid-cols-6 w-full gap-1 items-center border-b"
              :class="{
                'bg-red-50': product.priceDrift || product.stockDrift
              }"
            >
              <div>
                {{ product.nkrCode }}
                <span v-if="!product.onAmazon" class="text-red-500 text-xs">
                  ({{ $t("message.marketplaces.drift_not_on_amazon") }})
                </span>
              </div>
              <div class="text-center">
                {{
                  product.localPrice !== null ? "$" + product.localPrice : "—"
                }}
              </div>
              <div
                class="text-center"
                :class="{ 'text-red-600 font-bold': product.priceDrift }"
              >
                {{
                  product.amazonPrice !== null ? "$" + product.amazonPrice : "—"
                }}
              </div>
              <div class="text-center">
                {{ product.localStock !== null ? product.localStock : "—" }}
              </div>
              <div
                class="text-center"
                :class="{ 'text-red-600 font-bold': product.stockDrift }"
              >
                {{ product.amazonStock !== null ? product.amazonStock : "—" }}
              </div>
              <div class="text-center">{{ product.fba ? "FBA" : "FBM" }}</div>
            </div>
          </template>
        </div>
        <div class="footer pt-8 mb-10" v-if="!isCatalog">
          <div class="totals w-full flex justify-center mb-4">
            {{ $t("message.marketplaces.showing") }}
            {{ filters.limitPerPage * (filters.page - 1) }}
            {{ $t("message.marketplaces.to") }} {{ toQuantity() }}
            {{ $t("message.marketplaces.from") }} {{ totalItems }}
            {{ $t("message.marketplaces.results") }}
          </div>
          <div class="pages w-full flex flex-row justify-center">
            <select
              v-model="productsByPage"
              @change="
                (filters.limitPerPage = productsByPage), (filters.page = 1)
              "
              class="border-light-100 mr-4"
            >
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="500">500</option>
            </select>
            {{ $t("message.marketplaces.items_per_page") }}
          </div>
          <div class="pages flex flex-row w-full justify-center mt-4">
            <a
              :class="{ disabled: filters.page == 0 }"
              class="page-link mr-4 bg-gray-400 rounded-md px-2 text-white"
              @click="previous()"
              >{{ $t("message.marketplaces.previous") }}</a
            >
            <div>{{ $t("message.marketplaces.page") }} {{ filters.page }}</div>
            <a
              :class="{
                disabled: totalPages > filters.page
              }"
              class="page-link ml-4 bg-gray-400 rounded-md px-2 text-white"
              @click="next()"
              >{{ $t("message.marketplaces.next") }}</a
            >
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { debounce } from "lodash";
import {
  getAllMarketplaceItems,
  bulkSyncToAmazon,
  getAmazonJob,
  getAmazonPublishable,
  getAmazonDrift
} from "@/api/store/MarketplacesItemsService.js";
import ProductMarketplacesManagerItem from "@/components/product/ProductMarketplacesManagerItem.vue";
import AmazonCatalogReconciliation from "@/components/product/AmazonCatalogReconciliation.vue";

export default {
  name: "MarketplacesItems",
  components: {
    ProductMarketplacesManagerItem,
    AmazonCatalogReconciliation
  },
  data() {
    return {
      publicPath: process.env.VUE_APP_IMG_BASE_URL,
      ReportType: [
        {
          displayText: this.$t ? this.$t("message.marketplaces.all") : "All",
          value: "all"
        },
        {
          displayText: this.$t
            ? this.$t("message.marketplaces.disconnected")
            : "Disconnected",
          value: "disconnected"
        },
        {
          displayText: this.$t
            ? this.$t("message.marketplaces.no_weight")
            : "no_weight",
          value: "no_weight"
        },
        {
          displayText: this.$t
            ? this.$t("message.marketplaces.publishable")
            : "Publishable",
          value: "publishable"
        },
        {
          displayText: this.$t
            ? this.$t("message.marketplaces.drift")
            : "Drift",
          value: "drift"
        },
        {
          displayText: this.$t
            ? this.$t("message.marketplaces.catalog_tab")
            : "Amazon catalog",
          value: "amazon_catalog"
        }
      ],
      filters: {
        type: "all",
        page: 1,
        limitPerPage: "10",
        orderCol: "nkrCode",
        orderDir: "asc",
        search: "",
        marketplace: null
      },
      products: [],
      // Publicables: se cargan TODOS de una vez (query local barata, ~1800 filas)
      // y el filtrado/orden/paginado se hace en cliente para que buscar y cambiar
      // de página sea instantáneo (sin round-trips ni re-conteos por página).
      allPublishable: [],
      publishableLoaded: false,
      loadedType: null,
      publishableHeaders: [
        { value: "nkrCode", label: "sku_nkr", sortable: true },
        { value: "title", label: "product_title", sortable: true },
        {
          value: "stock",
          label: "publish_stock",
          sortable: true,
          center: true
        },
        {
          value: "sold",
          label: "publishable_sold",
          sortable: true,
          center: true
        },
        {
          value: "calculatedPrice",
          label: "publishable_price",
          sortable: true,
          center: true
        },
        { value: "actions", label: "actions", center: true }
      ],
      totalItems: 0,
      productsByPage: "10",
      totalPages: 0,
      loading: true,
      selectedProduct: null,
      openedProduct: "",
      showProductModal: false,
      bulkSyncing: false,
      bulkPollTimer: null
    };
  },
  watch: {
    filters: {
      deep: true,
      handler: debounce(function() {
        this.loading = true;
        const query = {
          ...this.$route.query,
          search: new URLSearchParams(this.filters).toString()
        };
        this.$router.replace({ query }).catch(() => {});

        this.loadProducts();
      }, 300)
    }
  },
  computed: {
    arrowDirection() {
      return this.filters.orderDir == "desc" ? "down" : "up";
    },
    userCanEditMarketplacesItems() {
      return this.$store.getters["auth/userHasPermission"](
        "can_edit_marketplaces_items"
      );
    },
    isPublishable() {
      return this.filters.type === "publishable";
    },
    isDrift() {
      return this.filters.type === "drift";
    },
    isCatalog() {
      return this.filters.type === "amazon_catalog";
    }
  },
  beforeDestroy() {
    if (this.bulkPollTimer) {
      clearInterval(this.bulkPollTimer);
    }
  },
  mounted() {
    if (this.$route.query.search) {
      let searchParams = new URLSearchParams(this.$route.query.search);
      for (let p of searchParams) {
        switch (p[1]) {
          case "true":
            p[1] = true;
            break;
          case "false":
            p[1] = false;
            break;
          case "null":
            p[1] = null;
            break;
          default:
            break;
        }
        this.filters[p[0]] = p[1];
      }
    }
    this.loadProducts();
    if (this.$route.query.productOpen) {
      this.openProduct(this.$route.query.productOpen);
    }
  },
  methods: {
    updateSearchFilter(search) {
      this.filters.page = 1;
      this.filters.search = search;
    },
    logout() {
      this.$store.dispatch("auth/logOut");
      this.$router.push("/");
    },
    updateStatusFilter(status) {
      this.filters.page = 1;
      this.filters.type = status;
    },
    async loadProducts() {
      // La pestaña de catálogo Amazon se autoabastece (componente propio).
      if (this.isCatalog) {
        this.loading = false;
        this.loadedType = "amazon_catalog";
        return;
      }
      const typeChanged = this.filters.type !== this.loadedType;

      // Publicables: traer TODO una sola vez y filtrar/paginar en cliente. Solo
      // se vuelve a pedir al servidor cuando se entra a la pestaña (cambio de
      // tipo); buscar, ordenar y cambiar de página no generan peticiones.
      if (this.isPublishable) {
        if (!typeChanged && this.publishableLoaded) {
          this.applyPublishablePaging();
          this.loading = false;
          return;
        }
        this.currentApiCallId = new Date();
        let apiId = this.currentApiCallId;
        // limitPerPage=0 => el backend omite el LIMIT y devuelve todo el catálogo.
        const res = await getAmazonPublishable({
          limitPerPage: 0
        }).catch(() => {
          console.log("Error getting Products");
        });
        if (apiId != this.currentApiCallId) {
          console.warn("another call is in process");
          return;
        }
        this.loadedType = "publishable";
        this.allPublishable = (res && res.products) || [];
        this.publishableLoaded = true;
        this.applyPublishablePaging();
        this.loading = false;
        return;
      }

      this.currentApiCallId = new Date();
      let apiId = this.currentApiCallId;
      // El resto de pestañas usan paginado en servidor: la vista normal el
      // reporte de marketplace items y "drift" la comparación viva con Amazon.
      const fetcher = this.isDrift ? getAmazonDrift : getAllMarketplaceItems;
      const res = await fetcher(this.filters).catch(() => {
        console.log("Error getting Products");
      });
      if (apiId != this.currentApiCallId) {
        console.warn("another call is in process");
        return;
      }
      if (!res) {
        this.loading = false;
        return;
      }
      this.loadedType = this.filters.type;
      this.products = res.products;
      this.totalItems = res.totalItems;
      this.totalPages = res.totalPages;
      this.loading = false;
    },
    // Aplica búsqueda + orden + paginado sobre allPublishable, sin red.
    applyPublishablePaging() {
      const search = (this.filters.search || "").toLowerCase().trim();
      let list = this.allPublishable;
      if (search) {
        list = list.filter(
          p =>
            (p.nkrCode && p.nkrCode.toLowerCase().includes(search)) ||
            (p.title && p.title.toLowerCase().includes(search))
        );
      }
      const dir = this.filters.orderDir === "desc" ? -1 : 1;
      const col = this.filters.orderCol || "nkrCode";
      const numericCols = ["stock", "sold", "calculatedPrice"];
      const isNum = numericCols.includes(col);
      list = list.slice().sort((a, b) => {
        if (isNum) {
          const av = parseFloat(a[col]) || 0;
          const bv = parseFloat(b[col]) || 0;
          return (av - bv) * dir;
        }
        const av = (a[col] || "").toString().toLowerCase();
        const bv = (b[col] || "").toString().toLowerCase();
        return av < bv ? -dir : av > bv ? dir : 0;
      });

      const per = parseInt(this.filters.limitPerPage, 10) || 20;
      this.totalItems = list.length;
      this.totalPages = per > 0 ? Math.ceil(list.length / per) || 1 : 1;
      // Si la búsqueda dejó menos páginas que la actual, volver a una válida.
      if (this.filters.page > this.totalPages) {
        this.filters.page = this.totalPages;
      }
      const start = (this.filters.page - 1) * per;
      this.products = list.slice(start, start + per);
    },
    previous() {
      if (this.filters.page > 1) {
        this.filters.page = this.filters.page - 1;
      }
    },
    next() {
      if (this.totalItems >= this.filters.limitPerPage * this.filters.page) {
        this.filters.page = this.filters.page + 1;
      }
    },
    toQuantity() {
      if (this.qtyPerPage * this.page > this.totalItems) {
        return this.products.length;
      } else {
        return (
          this.filters.limitPerPage * (this.filters.page - 1) +
          this.products.length
        );
      }
    },
    changeArrowDir(headerValue) {
      this.filters.page = 1;
      if (headerValue == this.filters.orderCol) {
        this.filters.orderDir =
          this.filters.orderDir == "desc" ? "asc" : "desc";
      } else {
        this.filters.orderCol = headerValue;
        this.filters.orderDir = "desc";
      }
    },
    openProduct(code) {
      this.openedProduct = code;
      this.showProductModal = true;
    },
    onCloseProduct() {
      this.openedProduct = "";
      // En "publicables", al cerrar el modal refrescamos: si se publicó el
      // producto ya tiene mapping y debe salir de la lista de publicables.
      if (this.isPublishable) {
        this.publishableLoaded = false;
        this.loadProducts();
      }
    },
    checkProductUpdate(product) {
      let productUpdated = this.products.find(
        p => p.nkrCode == product.nkrCode
      );
      if (productUpdated) {
        productUpdated.enabled = product.enabled;
      }
      // TODO - update any other column important
    },
    bulkSyncAmazon() {
      if (this.bulkSyncing) return;
      this.bulkSyncing = true;
      // Sincroniza todo el filtro Amazon actual (tipo + búsqueda) vía Feeds API.
      bulkSyncToAmazon({
        filters: { type: this.filters.type, search: this.filters.search }
      })
        .then(res => {
          this.$toast.open({
            message: this.$t("message.marketplaces.bulk_sync_started", {
              count: res.totalItems
            }),
            type: "success",
            position: "top-right"
          });
          if (res.jobId) {
            this.pollBulkJob(res.jobId);
          } else {
            this.bulkSyncing = false;
          }
        })
        .catch(e => {
          this.$toast.open({
            message:
              (e && e.response && e.response.data) ||
              this.$t("message.marketplaces.sync_error"),
            type: "error",
            position: "top-right"
          });
          this.bulkSyncing = false;
        });
    },
    pollBulkJob(jobId) {
      if (this.bulkPollTimer) clearInterval(this.bulkPollTimer);
      this.bulkPollTimer = setInterval(() => {
        getAmazonJob(jobId)
          .then(job => {
            if (!job || job.status === "done" || job.status === "error") {
              clearInterval(this.bulkPollTimer);
              this.bulkPollTimer = null;
              this.bulkSyncing = false;
              this.$toast.open({
                message:
                  job && job.status === "done"
                    ? this.$t("message.marketplaces.sync_success")
                    : this.$t("message.marketplaces.sync_error"),
                type: job && job.status === "done" ? "success" : "error",
                position: "top-right"
              });
              this.loadProducts();
            }
          })
          .catch(() => {
            clearInterval(this.bulkPollTimer);
            this.bulkPollTimer = null;
            this.bulkSyncing = false;
          });
      }, 5000);
    }
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";

.content-main {
  .content {
    .footer {
      display: flex;
      flex-direction: row;
      width: 100%;
      justify-content: space-between;
      .pages {
        display: flex;
        flex-direction: row;
        div {
          width: 75px;
        }
        .page-link {
          color: $tableLinkText;
        }
      }
    }
  }
}
</style>
